import { useZinsserStore } from '@/stores/useZinsserStore'
import { FileText, Paperclip, PenLine, FileDiff, ScanLine } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { ZinsserBrainstormDocumentContent } from './ZinsserBrainstormDocumentContent'
import { ZinsserFoolscapDocumentContent } from './ZinsserFoolscapDocumentContent'
import { ZinsserAttachmentsPanel } from './ZinsserAttachmentsPanel'
import { ZinsserAIChangesPanel } from './ZinsserAIChangesPanel'
import { ZinsserAnatomyPanel } from './ZinsserAnatomyPanel'

type TabType = 'brainstorm' | 'foolscap' | 'ai-changes' | 'anatomy' | 'attachments'

export function ZinsserDocumentPanel() {
  const {
    zinsserMode,
    sessionId,
    foolscapContent,
    foolscapDocumentPending,
    brainstormKeyThemes,
    brainstormGaps,
    workflowStatus,
    zinsserDocAnnotated,
    noChangesMessage,
    clearNoChangesMessage,
  } = useZinsserStore()

  const [activeTab, setActiveTab] = useState<TabType>('brainstorm')
  // Track if user manually switched tabs - don't auto-switch back while streaming
  const userSwitchedTabRef = useRef(false)
  const prevZinsserModeRef = useRef(zinsserMode)

  // Handle manual tab clicks - mark as user-initiated
  const handleTabClick = (tab: TabType) => {
    userSwitchedTabRef.current = true
    setActiveTab(tab)
  }

  // Reset to brainstorm tab when session changes (new session)
  useEffect(() => {
    setActiveTab('brainstorm')
    userSwitchedTabRef.current = false
  }, [sessionId])

  // Reset to brainstorm tab when foolscap content disappears (unless still in foolscap mode)
  useEffect(() => {
    if (!foolscapContent && activeTab === 'foolscap' && zinsserMode !== 'foolscap') {
      setActiveTab('brainstorm')
      userSwitchedTabRef.current = false
    }
  }, [foolscapContent, activeTab, zinsserMode])

  // Auto-switch to foolscap tab ONCE when entering foolscap mode
  // Don't auto-switch if user manually moved to another tab
  useEffect(() => {
    const justEnteredFoolscap = zinsserMode === 'foolscap' && prevZinsserModeRef.current !== 'foolscap'
    prevZinsserModeRef.current = zinsserMode

    if (justEnteredFoolscap && !userSwitchedTabRef.current) {
      setActiveTab('foolscap')
    }

    // Reset user switch flag when leaving foolscap mode
    if (zinsserMode !== 'foolscap') {
      userSwitchedTabRef.current = false
    }
  }, [zinsserMode])

  // Track previous workflow status to detect completion
  const prevWorkflowStatusRef = useRef(workflowStatus)
  const prevAnnotatedDocRef = useRef(zinsserDocAnnotated)

  // Auto-switch to Edits tab after workflow completes with edits
  // Handles both full workflow (streaming → idle) and standalone micro-editing (processing → idle)
  useEffect(() => {
    const wasActive = prevWorkflowStatusRef.current === 'streaming' || prevWorkflowStatusRef.current === 'processing'
    const justCompleted = workflowStatus === 'idle' && wasActive
    const annotatedDocJustArrived = zinsserDocAnnotated && !prevAnnotatedDocRef.current

    prevWorkflowStatusRef.current = workflowStatus
    prevAnnotatedDocRef.current = zinsserDocAnnotated

    // Switch to Edits tab when:
    // 1. Workflow just completed AND annotated doc exists, OR
    // 2. Annotated doc just arrived AND workflow is idle (doc came after completion)
    if ((justCompleted && zinsserDocAnnotated) || (annotatedDocJustArrived && workflowStatus === 'idle')) {
      setActiveTab('ai-changes')
      userSwitchedTabRef.current = false
    }
  }, [workflowStatus, zinsserDocAnnotated])

  // Auto-clear "no changes" message after 3 seconds
  useEffect(() => {
    if (!noChangesMessage) return
    const timer = setTimeout(() => {
      clearNoChangesMessage()
    }, 3000)
    return () => clearTimeout(timer)
  }, [noChangesMessage, clearNoChangesMessage])

  // Show Foolscap tab when content exists OR when in foolscap mode (preparing/pending)
  const showFoolscapTab = Boolean(foolscapContent) || zinsserMode === 'foolscap' || foolscapDocumentPending
  // Canvas is editable in brainstorm and gatekeeper modes (brainstorm for old sessions, gatekeeper for new)
  const isBrainstormReadOnly = zinsserMode !== 'brainstorm' && zinsserMode !== 'gatekeeper'

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-3 md:px-2 py-2 md:py-1.5 border-b border-border overflow-x-auto">
        {/* Brainstorm tab */}
        <button
          onClick={() => handleTabClick('brainstorm')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'brainstorm'
              ? 'bg-primary text-primary-foreground font-medium'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
        >
          <PenLine className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Canvas</span>
          {isBrainstormReadOnly && (
            <span className="ml-1 text-xs text-muted-foreground">(read-only)</span>
          )}
        </button>

        {/* Foolscap tab - only show when content exists */}
        {showFoolscapTab && (
          <button
            onClick={() => handleTabClick('foolscap')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
              activeTab === 'foolscap'
                ? 'bg-primary text-primary-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Foolscap</span>
          </button>
        )}

        {/* AI Changes tab - always visible */}
        <button
          onClick={() => handleTabClick('ai-changes')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'ai-changes'
              ? 'bg-primary text-primary-foreground font-medium'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
        >
          <FileDiff className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Edits</span>
        </button>

        {/* Anatomy tab - always visible */}
        <button
          onClick={() => handleTabClick('anatomy')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'anatomy'
              ? 'bg-primary text-primary-foreground font-medium'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
        >
          <ScanLine className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Anatomy</span>
        </button>

        {/* Attachments tab - always visible */}
        <button
          onClick={() => handleTabClick('attachments')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'attachments'
              ? 'bg-primary text-primary-foreground font-medium'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
        >
          <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Attachments</span>
        </button>

      </div>

      {/* Metadata bar (themes, gaps) - only for brainstorm, hidden on mobile */}
      {activeTab === 'brainstorm' && (brainstormKeyThemes.length > 0 || brainstormGaps.length > 0) && (
        <div className="hidden md:block flex-shrink-0 px-4 py-2 text-xs border-b border-border">
          {brainstormKeyThemes.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {brainstormKeyThemes.map((theme, i) => (
                <span key={i} className="px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                  {theme}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(brainstormGaps) && brainstormGaps.length > 0 && (
            <div className="mt-1.5 text-muted-foreground">
              <span className="font-medium">Gaps:</span> {brainstormGaps.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Document content - always mount to preserve editor state, hide with CSS */}
      {/* key={sessionId} forces editor remount on session change, resetting undo history */}
      <div className="flex-1 overflow-hidden relative">
        <div className={`absolute inset-0 ${activeTab === 'brainstorm' ? '' : 'invisible pointer-events-none'}`}>
          <ZinsserBrainstormDocumentContent key={sessionId} editable={!isBrainstormReadOnly} />
        </div>
        <div className={`absolute inset-0 ${activeTab === 'foolscap' ? '' : 'invisible pointer-events-none'}`}>
          <ZinsserFoolscapDocumentContent key={sessionId} editable={zinsserMode === 'foolscap'} />
        </div>
        <div className={`absolute inset-0 ${activeTab === 'ai-changes' ? '' : 'invisible pointer-events-none'}`}>
          <ZinsserAIChangesPanel key={sessionId} />
        </div>
        <div className={`absolute inset-0 ${activeTab === 'anatomy' ? '' : 'invisible pointer-events-none'}`}>
          <ZinsserAnatomyPanel key={sessionId} />
        </div>
        <div className={`absolute inset-0 ${activeTab === 'attachments' ? '' : 'invisible pointer-events-none'}`}>
          <ZinsserAttachmentsPanel key={sessionId} />
        </div>

        {/* Temporary "no changes" message toast */}
        {noChangesMessage && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-2 bg-muted border border-border rounded-lg shadow-lg text-sm text-muted-foreground">
              {noChangesMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
